import { useHover } from '@react-aria/interactions';
import { createArrowRight } from '@shared/assets/icons';
import {
  Checkbox,
  DropdownMenu,
  Icon,
  Text,
  TextField,
  Tooltip,
} from '@shared/components';
import { SearchFieldRow } from '@shared/components/search-field/SearchFieldRow';
import {
  makeElementClassNameFactory,
  makeRootClassName,
  StyleProps,
} from '@shared/utils';
import { DropdownQueryParam } from 'clerk_common/constants/urls/queryParams';
import clsx from 'clsx';
import { useQueryState } from 'nuqs';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { PiInfoBold } from 'react-icons/pi';
import { FixedSizeList as List } from 'react-window';
import { FILTER_SUB_CONTENT_CLASS_NAME } from '../../classNames';
import useFilters from '../../hooks/useFilters';
import { DropdownFilterOption, ListType } from '../../types';

export type DropdownMenuItemProps = StyleProps & {
  /**
   * Whether to show the search field.
   * @default false
   */
  showSearch?: boolean;

  /**
   * Additional search processing.
   * @param query The query to search.
   * @returns
   */
  handleSearch?: (query?: string) => void;

  /**
   * Whether the items are loading from a backend query.
   * @default false
   */
  isLoading?: boolean;

  /**
   * Whether or not to show the min/max input fields.
   * @default false
   */
  showMinMax?: boolean;
  /**
   * Whether or not to show a single number input field.
   * @default false
   */
  isNumberInput?: boolean;
  /** The options to display in the sub menu. */
  options: DropdownFilterOption;
  /** The type of query to update when checking/unchecking. */
  type: DropdownQueryParam;
  /** Whether or not the menu is open. */
  open: boolean;
  /** Method to set the menu open state. */
  setOpen: (open: boolean) => void;
  /**
   * Whether or not to exclude the `Any` option.
   * @default false
   * */
  excludeAny?: boolean;
};

const ROOT = makeRootClassName('DropdownMenuItem');
const el = makeElementClassNameFactory(ROOT);

const DEFAULT_PROPS = {
  showSearch: false,
  showMinMax: false,
  isNumberInput: false,
  excludeAny: false,
} as const;

type MinMaxRowProps = {
  isOpen: boolean;
  type: DropdownQueryParam;
};

type NumberInputRowProps = {
  type: DropdownQueryParam;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
};

type CheckboxRowProps = {
  content: string;
  value: string;
  type: DropdownQueryParam;
  isChecked: boolean;
  isDisabled: boolean;
  onToggle: (value: string, checked: boolean) => void;
  style?: React.CSSProperties;
};

function NumberInputRow(props: NumberInputRowProps): ReactElement {
  const p = { ...props };
  const [value, setValue] = useQueryState(p.type, {
    defaultValue: '',
    parse: (val) => val || '',
    serialize: (val) => val || '',
  });

  const [localValue, setLocalValue] = useState(value ?? '');

  useEffect(() => {
    setLocalValue(value ?? '');
  }, [value]);

  useEffect(() => {
    if (!p.isOpen && localValue !== value) {
      const trimmedValue = localValue.trim();
      if (trimmedValue === '' || /^\d+$/.test(trimmedValue)) {
        setValue(trimmedValue === '' ? null : trimmedValue, {
          shallow: true,
        });
      }
    }
  }, [p.isOpen, localValue, value, setValue]);

  const handleChange = (newValue: string) => {
    if (newValue === '' || /^\d+$/.test(newValue)) {
      setLocalValue(newValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      p.setOpen(false);
    }
  };

  return (
    <div className="p-[8px]">
      <TextField
        aria-label="Number input"
        type="number"
        value={localValue}
        onChange={handleChange}
        placeholder="Enter number"
        className="w-full"
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}

function MinMaxRow(props: MinMaxRowProps): ReactElement {
  const p = { ...props };
  const { getMinMaxAmount, setMinMaxAmount } = useFilters();

  const minAmountFromParams = getMinMaxAmount(
    DropdownQueryParam.CUSTOM_MIN_VALUE
  );
  const maxAmountFromParams = getMinMaxAmount(
    DropdownQueryParam.CUSTOM_MAX_VALUE
  );

  const [isCustomValue, setIsCustomValue] = useState(
    minAmountFromParams || maxAmountFromParams ? true : false
  );
  const [minValue, setMinValue] = useState(minAmountFromParams ?? '');
  const [maxValue, setMaxValue] = useState(maxAmountFromParams ?? '');

  const handleCheckedChange = (checked: boolean) => {
    if (!checked) {
      setMinMaxAmount({
        checked: false,
        minAmount: '',
        maxAmount: '',
        minParam: DropdownQueryParam.CUSTOM_MIN_VALUE,
        maxParam: DropdownQueryParam.CUSTOM_MAX_VALUE,
      });
      setMinValue('');
      setMaxValue('');
      setIsCustomValue(false);
    } else {
      setIsCustomValue(true);
    }
  };

  useEffect(() => {
    if (!p.isOpen && isCustomValue) {
      setMinMaxAmount({
        checked: isCustomValue,
        minAmount: minValue,
        maxAmount: maxValue,
        minParam: DropdownQueryParam.CUSTOM_MIN_VALUE,
        maxParam: DropdownQueryParam.CUSTOM_MAX_VALUE,
      });
    }
  }, [p.isOpen, isCustomValue]);

  return (
    <div>
      <div
        className={clsx(el`checkbox-row`, {
          'is-checked': isCustomValue,
        })}
      >
        <Checkbox
          checked={isCustomValue}
          onCheckedChange={(checked: boolean) => handleCheckedChange(checked)}
        />
        <Text>Custom</Text>
      </div>
      <div className="flex flex-row p-[8px]">
        <TextField
          aria-label="Min amount"
          type="number"
          value={minValue}
          onChange={setMinValue}
          isDisabled={!isCustomValue}
          placeholder="Min"
          className="mr-[4px] w-[90px]"
        />
        <TextField
          aria-label="Max amount"
          type="number"
          value={maxValue}
          onChange={setMaxValue}
          isDisabled={!isCustomValue}
          placeholder="Max"
          className="ml-[4px w-[90px]"
        />
      </div>
    </div>
  );
}

export function CheckboxRow(props: CheckboxRowProps): ReactElement {
  const { content, value, isChecked, isDisabled, onToggle, style } = props;

  return (
    <div
      className={clsx(el`checkbox-row`, {
        'is-checked': isChecked,
      })}
      style={style}
      title={content}
    >
      <Checkbox
        disabled={isDisabled}
        checked={isChecked}
        onCheckedChange={(checked) => onToggle(value, checked === true)}
      />
      <Text className="min-w-0 flex-1 truncate">{content}</Text>
    </div>
  );
}

export function deduplicateByValue(options: ListType[]) {
  const seenValues = new Set<string>();
  const deduped: ListType[] = [];

  for (const option of options) {
    if (!seenValues.has(option.value)) {
      seenValues.add(option.value);
      deduped.push(option);
    }
  }

  if (deduped.length !== options.length) {
    console.warn(
      'Duplicate-valued options were provided to DropdownMenuItem component.',
      'They have been deduplicated, so you might not see all the options you expect.',
      'This likely indicates a bug in the code that generates the option list passed in.'
    );
  }
  return deduped;
}

export const CHECKBOX_ROW_HEIGHT = 36;
const MAX_VISIBLE_ROWS = 11;
// NOTE(dev): Above this count, render rows through react-window so that a
// filter with thousands of options does not freeze the tab by mounting every row at once.
export const VIRTUALIZATION_THRESHOLD = 50;

export function shouldVirtualizeOptions(optionCount: number): boolean {
  return optionCount > VIRTUALIZATION_THRESHOLD;
}

type DropdownOptionsListProps = {
  options: ListType[];
  renderOption: (option: ListType, style?: React.CSSProperties) => ReactElement;
};

export function DropdownOptionsList({
  options,
  renderOption,
}: DropdownOptionsListProps): ReactElement {
  if (!shouldVirtualizeOptions(options.length)) {
    return <>{options.map((option) => renderOption(option))}</>;
  }

  const listHeight = Math.min(
    options.length * CHECKBOX_ROW_HEIGHT,
    MAX_VISIBLE_ROWS * CHECKBOX_ROW_HEIGHT
  );

  return (
    <List
      height={listHeight}
      itemCount={options.length}
      itemSize={CHECKBOX_ROW_HEIGHT}
      width="100%"
    >
      {({ index, style }: { index: number; style: React.CSSProperties }) => {
        const option = options[index];
        if (!option) return null;
        return renderOption(option, style);
      }}
    </List>
  );
}

export function FilterSubMenuTrigger({
  label,
  tooltipContent,
}: {
  label: string;
  tooltipContent?: string;
}): ReactElement {
  const [isHovered, setIsHovered] = useState(false);
  const { hoverProps } = useHover({
    onHoverStart: () => setIsHovered(true),
    onHoverEnd: () => setIsHovered(false),
  });
  return (
    <DropdownMenu.SubTrigger>
      <button
        {...hoverProps}
        className={clsx(el`sub-content-row`, {
          'is-hovered': isHovered,
        })}
      >
        <div className={el`sub-content-label`}>
          <Text>{label}</Text>
          {tooltipContent ? (
            <Tooltip
              className={el`tooltip-content`}
              content={tooltipContent}
              isInstant
            >
              <div>
                <PiInfoBold size={14} />
              </div>
            </Tooltip>
          ) : null}
        </div>
        <div>
          <Icon content={createArrowRight} size="xs" />
        </div>
      </button>
    </DropdownMenu.SubTrigger>
  );
}

function DropdownMenuItem(props: DropdownMenuItemProps): ReactElement {
  const p = { ...DEFAULT_PROPS, ...props };
  const uniqueOptions = useMemo(
    () => deduplicateByValue(p.options.options),
    [p.options.options]
  );
  const [options, setOptions] = useState(uniqueOptions);
  const [searchValue, setSearchValue] = useState('');

  const { checkIfValueIsSelected, handleUpdateIdSelection, getMinMaxAmount } =
    useFilters();

  const isMinMaxActive =
    p.type.includes('amount') &&
    !!(
      getMinMaxAmount(DropdownQueryParam.CUSTOM_MIN_VALUE) ||
      getMinMaxAmount(DropdownQueryParam.CUSTOM_MAX_VALUE)
    );

  const handleToggle = (value: string, checked: boolean) => {
    handleUpdateIdSelection({ id: value, checked, queryParam: p.type });
  };

  // NOTE(dev): Resolve selection per rendered row instead of pre-scanning
  // every option. Combined with virtualization, only the visible rows compute
  // selection state, so a large list does not iterate on render.
  const renderOption = (option: ListType, style?: React.CSSProperties) => (
    <CheckboxRow
      key={`${p.type}-${option.value}`}
      type={p.type}
      content={option.label}
      value={option.value}
      isChecked={
        (checkIfValueIsSelected(option.value, p.type) ?? false) &&
        !isMinMaxActive
      }
      isDisabled={isMinMaxActive}
      onToggle={handleToggle}
      style={style}
    />
  );

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (p.handleSearch) {
      p.handleSearch(value);
    } else {
      setOptions(
        uniqueOptions.filter((o) => {
          const { label } = o;
          return label.toLowerCase().includes(value.toLowerCase());
        })
      );
    }
  };

  // TODO(max): Currently the backend queries are fast enough
  // that we just present the original data until the new query
  // finishes instead of flicker for aesthetic reasons.
  useEffect(() => {
    if (!p.isLoading) {
      setOptions(uniqueOptions);
    }
  }, [uniqueOptions, p.isLoading]);

  useEffect(() => {
    return () => handleSearchChange?.('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldVirtualize = shouldVirtualizeOptions(options.length);

  return (
    <DropdownMenu.Sub>
      <FilterSubMenuTrigger
        label={p.options.label}
        tooltipContent={p.options.tooltipContent}
      />
      <DropdownMenu.SubContent
        className={FILTER_SUB_CONTENT_CLASS_NAME}
        style={
          shouldVirtualize
            ? { maxHeight: 'none', overflow: 'hidden' }
            : undefined
        }
      >
        {p.showSearch && (
          <div className={el`filter-row`}>
            <SearchFieldRow
              value={searchValue}
              onValueChange={handleSearchChange}
            />
          </div>
        )}
        {p.isNumberInput ? (
          <NumberInputRow type={p.type} isOpen={p.open} setOpen={p.setOpen} />
        ) : (
          <>
            <DropdownOptionsList
              options={options}
              renderOption={renderOption}
            />
            {p.showMinMax && <MinMaxRow type={p.type} isOpen={p.open} />}
          </>
        )}
      </DropdownMenu.SubContent>
    </DropdownMenu.Sub>
  );
}

export default DropdownMenuItem;
